import React, { useState, useEffect, useRef, useMemo } from "react";
import { useQuery } from "@tanstack/react-query";
import { api, apiError, API } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { useBilling } from "@/context/BillingContext";
import { money } from "@/lib/format";
import { cacheProducts, getCachedProducts, isOnline, queueSale, syncQueue } from "@/lib/offline";
import { printThermal, printA4, shareWhatsApp, autoPrint } from "@/lib/print";
import { BarcodeScanner } from "@/components/BarcodeScanner";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { ScrollArea } from "@/components/ui/scroll-area";
import { ShoppingCart, Search, Plus, Minus, Trash2, X, Barcode, UserPlus, Printer, Share2, CheckCircle2, Package, Camera, FileText, Star, Loader2 } from "lucide-react";
import { toast } from "sonner";

export default function Billing() {
  const { user, hasPerm } = useAuth();
  const { state, update, reset } = useBilling();
  const { cart, customer, billDiscount, taxInclusive, paymentMethod, paidAmount, customValue } = state;
  const [query, setQuery] = useState("");
  const [addOpen, setAddOpen] = useState(false);
  const [scanOpen, setScanOpen] = useState(false);
  const [newCustOpen, setNewCustOpen] = useState(false);
  const [newCust, setNewCust] = useState({ name: "", phone: "", address: "", credit_limit: 0 });
  const [lastSale, setLastSale] = useState(null);
  const [successOpen, setSuccessOpen] = useState(false);
  const [saving, setSaving] = useState(false);
  const [quoteMode, setQuoteMode] = useState(false);
  const [createOpen, setCreateOpen] = useState(false);
  const [newItem, setNewItem] = useState({ name: "", selling_price: "", tax_rate: 0, hsn: "", unit: "PCS", is_service: false });
  const searchRef = useRef(null);
  const fileRef = useRef(null);
  const [ocrLoading, setOcrLoading] = useState(false);
  const [ocrReview, setOcrReview] = useState(null);

  const { data: favData } = useQuery({ queryKey: ["pos-favorites"], queryFn: async () => (await api.get("/products", { params: { favorites: true, limit: 100 } })).data });
  const { data: allProd } = useQuery({ queryKey: ["pos-all"], queryFn: async () => (await api.get("/products", { params: { limit: 1000 } })).data });
  const { data: customers } = useQuery({ queryKey: ["pos-customers"], queryFn: async () => (await api.get("/customers")).data });
  const { data: settings } = useQuery({ queryKey: ["all-settings"], queryFn: async () => (await api.get("/settings")).data });

  useEffect(() => { if (allProd?.items) cacheProducts(allProd.items); }, [allProd]);
  const products = allProd?.items || getCachedProducts();
  const favorites = favData?.items || [];
  const custom_label = settings?.bill?.custom_label;
  useEffect(() => { if (cart.some((x) => !x.key)) update({ cart: cart.map((x) => (x.key ? x : { ...x, key: cartKey() })) }); }, []); // eslint-disable-line

  const searchResults = useMemo(() => {
    if (!query) return [];
    const q = query.toLowerCase();
    return products.filter((p) => p.name.toLowerCase().includes(q) || (p.sku || "").toLowerCase().includes(q) || (p.barcode || "").includes(q)).slice(0, 40);
  }, [query, products]);

  function cartKey() { return Math.random().toString(36).slice(2, 10); }
  function imgUrl(p) { return p.image ? (p.image.startsWith("http") ? p.image : `${API}/files/${p.image}`) : null; }

  function addToCart(p) {
    const idx = p.id ? cart.findIndex((x) => x.product_id === p.id) : -1;
    if (idx >= 0) { const c = [...cart]; c[idx] = { ...c[idx], qty: c[idx].qty + 1 }; update({ cart: c }); }
    else update({ cart: [...cart, { key: cartKey(), product_id: p.id || null, name: p.name, hsn: p.hsn, qty: 1, price: p.selling_price, discount: 0, tax_rate: p.tax_rate, unit: p.unit, image: p.image }] });
  }
  function addByCode(code) {
    const exact = products.find((p) => p.barcode === code || p.sku === code);
    if (exact) { addToCart(exact); toast.success(`${exact.name} added`); }
    else toast.error("Product not found for this barcode");
  }
  function updateQty(k, delta) { update({ cart: cart.map((x) => x.key === k ? { ...x, qty: Math.max(1, x.qty + delta) } : x) }); }
  function setLine(k, f, v) { update({ cart: cart.map((x) => x.key === k ? { ...x, [f]: v } : x) }); }
  function removeLine(k) { update({ cart: cart.filter((x) => x.key !== k) }); }

  const totals = useMemo(() => {
    let subtotal = 0, tax = 0;
    cart.forEach((it) => {
      const line = it.qty * Number(it.price) - Number(it.discount || 0);
      if (taxInclusive) { const base = it.tax_rate ? line / (1 + it.tax_rate / 100) : line; tax += line - base; subtotal += base; }
      else { subtotal += line; tax += line * it.tax_rate / 100; }
    });
    return { subtotal, tax, grand: Math.max(0, subtotal + tax - Number(billDiscount || 0)) };
  }, [cart, taxInclusive, billDiscount]);

  const showPaid = paymentMethod === "credit";
  const dueAmount = showPaid ? Math.max(0, totals.grand - Number(paidAmount || 0)) : 0;

  async function checkout() {
    if (!cart.length) { toast.error("Cart is empty"); return; }
    if (!quoteMode && !paymentMethod) { toast.error("Please select a payment method"); return; }
    setSaving(true);
    const base = {
      items: cart.map((it) => ({ product_id: it.product_id, name: it.name, hsn: it.hsn, qty: Number(it.qty), price: Number(it.price), discount: Number(it.discount || 0), tax_rate: it.tax_rate, unit: it.unit })),
      customer_id: customer?.id || null, customer_name: customer?.name || (quoteMode ? "Customer" : "Walk-in Customer"),
      customer_phone: customer?.phone || "", bill_discount: Number(billDiscount || 0), tax_inclusive: taxInclusive,
      note: "", custom_value: customValue || "",
    };
    if (quoteMode) {
      try {
        const { data } = await api.post("/quotations", base);
        setLastSale({ ...data, _quote: true }); setSuccessOpen(true); reset();
        toast.success("Quotation created");
      } catch (e) { toast.error(apiError(e)); } finally { setSaving(false); }
      return;
    }
    const payload = { ...base, payment_method: paymentMethod, paid_amount: paymentMethod === "credit" ? Number(paidAmount || 0) : (paymentMethod ? totals.grand : 0), send_bill: !!state.sendBill, local_id: `${Date.now()}-${Math.random().toString(36).slice(2, 8)}` };
    if (!isOnline()) {
      queueSale(payload);
      const off = { ...payload, bill_no: "OFFLINE-" + payload.local_id.slice(-5), subtotal: totals.subtotal, tax_total: totals.tax, grand_total: totals.grand, created_at: new Date().toISOString(), outstanding: Math.max(0, totals.grand - payload.paid_amount) };
      setLastSale(off); setSuccessOpen(true); reset(); setSaving(false);
      autoPrint(off, settings);
      toast.warning("Offline: bill saved locally, will sync when online");
      return;
    }
    try {
      const { data } = await api.post("/sales", payload);
      setLastSale(data); setSuccessOpen(true); reset();
      toast.success(`Bill ${data.bill_no} generated`);
      if (data.send_status && !data.send_status.ok) {
        const reason = data.send_status.reason === "no_mobile" ? "no customer mobile" : data.send_status.reason === "sms_not_configured" ? "SMS not configured" : "SMS failed";
        toast.warning(`Bill saved, but not sent: ${reason}`);
      } else if (data.send_status?.ok) { toast.success("Bill sent by SMS"); }
      autoPrint(data, settings); // auto print after save
      syncQueue();
    } catch (e) { toast.error(apiError(e)); } finally { setSaving(false); }
  }

  async function createCustomer() {
    try {
      const { data } = await api.post("/customers", { ...newCust, credit_limit: Number(newCust.credit_limit || 0) });
      update({ customer: data }); setNewCustOpen(false); setNewCust({ name: "", phone: "", address: "", credit_limit: 0 });
      toast.success("Customer added");
    } catch (e) { toast.error(apiError(e)); }
  }

  async function createItem() {
    if (!newItem.name) { toast.error("Item name is required"); return; }
    try {
      const body = {
        name: newItem.name, selling_price: Number(newItem.selling_price || 0), tax_rate: Number(newItem.tax_rate || 0),
        hsn: newItem.hsn, unit: newItem.is_service ? "SVC" : (newItem.unit || "PCS"),
        is_service: newItem.is_service, tax_type: Number(newItem.tax_rate) > 0 ? "gst" : "none",
        opening_stock: 0, current_stock: 0,
      };
      const { data } = await api.post("/products", body);
      addToCart(data); // returns to the same bill; cart/customer/discount state preserved
      setCreateOpen(false);
      setNewItem({ name: "", selling_price: "", tax_rate: 0, hsn: "", unit: "PCS", is_service: false });
      toast.success(`${data.name} created & added${data.is_service ? " (service, no stock)" : ""}`);
    } catch (e) { toast.error(apiError(e)); }
  }

  async function parseOcr(e) {
    const file = e.target.files?.[0]; e.target.value = "";
    if (!file) return; setOcrLoading(true);
    try {
      const fd = new FormData(); fd.append("file", file);
      const { data } = await api.post("/ocr/parse-bill", fd, { headers: { "Content-Type": "multipart/form-data" } });
      setOcrReview({ ...data.parsed, preview: data.preview });
      toast.success("Bill scanned · review before adding to cart");
    } catch (err) { toast.error(apiError(err)); } finally { setOcrLoading(false); }
  }
  function updOcr(i, k, v) { setOcrReview((r) => ({ ...r, items: r.items.map((it, x) => x === i ? { ...it, [k]: v } : it) })); }
  function rmOcr(i) { setOcrReview((r) => ({ ...r, items: r.items.filter((_, x) => x !== i) })); }
  function applyOcr() {
    const newLines = (ocrReview.items || []).map((it) => ({ key: cartKey(), product_id: null, name: it.name || "Item", qty: Number(it.qty) || 1, price: Number(it.rate) || 0, discount: Number(it.discount) || 0, tax_rate: Number(it.tax_rate) || 0, unit: it.unit || "PCS" }));
    update({ cart: [...cart, ...newLines] });
    setOcrReview(null);
    toast.success(`${newLines.length} item(s) added — verify price & qty`);
  }

  const biz = settings?.business || {}, bill = settings?.bill || {}, printer = settings?.printer || {};

  const ProductBtn = (p) => {
    const out = p.current_stock <= 0;
    return (
      <button key={p.id} onClick={() => { addToCart(p); }} data-testid={`pos-product-${p.id}`}
        className="text-left bg-white rounded-xl border border-slate-200 p-3 hover:border-[#F4571E] hover:shadow-md transition-all active:scale-[0.98]">
        <div className="h-10 w-10 rounded-lg bg-[#1A2A5E]/8 flex items-center justify-center mb-2 overflow-hidden">
          {imgUrl(p) ? <img src={imgUrl(p)} alt="" className="h-full w-full object-cover" /> : <Package className="h-4 w-4 text-[#1A2A5E]" />}
        </div>
        <div className="font-medium text-sm text-slate-800 line-clamp-2 min-h-[2.5rem]">{p.name}</div>
        <div className="flex items-center justify-between mt-1.5">
          <span className="font-mono font-bold text-[#F4571E]">{money(p.selling_price)}</span>
          <Badge variant="secondary" className={`text-[10px] ${out ? "bg-rose-50 text-rose-600" : ""}`}>{p.current_stock} {p.unit}</Badge>
        </div>
      </button>
    );
  };

  return (
    <div className="grid lg:grid-cols-[1fr_420px] xl:grid-cols-[1fr_460px] gap-4 md:gap-6 -mt-1">
      <div className="space-y-4 min-w-0">
        <div className="flex flex-wrap gap-2">
          <div className="relative flex-1 min-w-[240px]">
            <Barcode className="absolute left-3 top-1/2 -translate-y-1/2 h-5 w-5 text-[#F4571E]" />
            <Input ref={searchRef} autoFocus data-testid="pos-barcode-input" value={query}
              onChange={(e) => setQuery(e.target.value)}
              onKeyDown={(e) => { if (e.key === "Enter" && query.trim()) { addByCode(query.trim()); setQuery(""); } }}
              placeholder="Scan barcode or type to search..." className="pl-11 h-12 text-base bg-white focus-visible:ring-2 focus-visible:ring-[#F4571E]" />
          </div>
          {hasPerm("barcode_scanner") && <Button variant="outline" className="h-12 gap-2" onClick={() => setScanOpen(true)} data-testid="open-camera-scan"><Camera className="h-5 w-5" /></Button>}
          <Button variant="outline" className="h-12 gap-2" onClick={() => setAddOpen(true)} data-testid="add-item-btn"><Plus className="h-5 w-5" /> Add Item</Button>
          {hasPerm("create_product") && <Button variant="outline" className="h-12 gap-2 border-[#1A2A5E] text-[#1A2A5E]" onClick={() => setCreateOpen(true)} data-testid="create-item-btn"><Package className="h-5 w-5" /> Create Item</Button>}
          {hasPerm("ocr_import") && <>
            <input ref={fileRef} type="file" accept=".jpg,.jpeg,.png,.pdf,image/*,application/pdf" className="hidden" onChange={parseOcr} data-testid="pos-ocr-input" />
            <Button variant="outline" className="h-12 gap-2 border-emerald-500 text-emerald-700" onClick={() => fileRef.current?.click()} disabled={ocrLoading} data-testid="pos-upload-bill">{ocrLoading ? <Loader2 className="h-5 w-5 animate-spin" /> : <FileText className="h-5 w-5" />} Upload Bill</Button>
          </>}
          <Button variant="outline" className="h-12 gap-2 shrink-0 whitespace-nowrap border-purple-400 text-purple-700" onClick={() => window.location.assign("/detailed-invoice")} data-testid="detailed-invoice-btn"><FileText className="h-5 w-5" /> Detailed Invoice</Button>
        </div>

        {query ? (
          <div className="grid grid-cols-2 sm:grid-cols-3 xl:grid-cols-4 gap-3">
            {searchResults.map(ProductBtn)}
            {!searchResults.length && <div className="col-span-full text-center py-16 text-slate-400"><Search className="h-8 w-8 mx-auto mb-2 opacity-40" />No products match</div>}
          </div>
        ) : (
          <div>
            <div className="flex items-center gap-2 mb-3 text-sm font-semibold text-[#1A2A5E]"><Star className="h-4 w-4 text-[#F4571E] fill-[#F4571E]" /> Favorite Items</div>
            <div className="grid grid-cols-2 sm:grid-cols-3 xl:grid-cols-4 gap-3">
              {favorites.map(ProductBtn)}
              {!favorites.length && <div className="col-span-full text-center py-14 text-slate-400"><Star className="h-8 w-8 mx-auto mb-2 opacity-40" />No favorites yet. Mark products as favorite in Inventory, or use search / Add Item.</div>}
            </div>
          </div>
        )}
      </div>

      <Card className="border-slate-200 flex flex-col lg:sticky lg:top-24 lg:h-[calc(100vh-8rem)]">
        <div className="p-4 border-b border-slate-100 flex items-center justify-between">
          <div className="flex items-center gap-2 font-display font-semibold text-[#1A2A5E]">{quoteMode ? <FileText className="h-5 w-5" /> : <ShoppingCart className="h-5 w-5" />} {quoteMode ? "Quotation" : "Current Bill"}</div>
          <div className="flex items-center gap-1">
            {hasPerm("quotation") && <Button variant={quoteMode ? "default" : "ghost"} size="sm" className={`h-8 ${quoteMode ? "bg-[#1A2A5E]" : ""}`} onClick={() => setQuoteMode(!quoteMode)} data-testid="quote-toggle"><FileText className="h-4 w-4 mr-1" />Quote</Button>}
            {cart.length > 0 && <Button variant="ghost" size="sm" className="text-red-500 h-8" onClick={reset} data-testid="clear-cart"><Trash2 className="h-4 w-4 mr-1" /> Clear</Button>}
          </div>
        </div>

        <div className="p-3 border-b border-slate-100 flex gap-2">
          <Select value={customer?.id || "walkin"} onValueChange={(v) => update({ customer: v === "walkin" ? null : (customers || []).find((c) => c.id === v) })}>
            <SelectTrigger className="flex-1" data-testid="pos-customer-select"><SelectValue /></SelectTrigger>
            <SelectContent className="bg-white/95 backdrop-blur-md">
              <SelectItem value="walkin">Walk-in Customer</SelectItem>
              {(customers || []).map((c) => <SelectItem key={c.id} value={c.id}>{c.name} {c.phone && `· ${c.phone}`}</SelectItem>)}
            </SelectContent>
          </Select>
          <Button variant="outline" size="icon" onClick={() => setNewCustOpen(true)} data-testid="pos-new-customer"><UserPlus className="h-4 w-4" /></Button>
        </div>

        <ScrollArea className="flex-1 min-h-[120px]">
          <div className="p-3 space-y-2">
            {cart.length ? cart.map((it) => (
              <div key={it.key} className="bg-slate-50 rounded-lg p-2.5" data-testid={`cart-item-${it.key}`}>
                <div className="flex items-start justify-between gap-2">
                  <div className="font-medium text-sm text-slate-800 flex-1">{it.name}</div>
                  <button onClick={() => removeLine(it.key)}><X className="h-4 w-4 text-slate-400" /></button>
                </div>
                <div className="flex items-center justify-between mt-2 gap-2">
                  <div className="flex items-center gap-1">
                    <Button size="icon" variant="outline" className="h-7 w-7" onClick={() => updateQty(it.key, -1)}><Minus className="h-3 w-3" /></Button>
                    <span className="w-8 text-center font-mono text-sm">{it.qty}</span>
                    <Button size="icon" variant="outline" className="h-7 w-7" onClick={() => updateQty(it.key, 1)}><Plus className="h-3 w-3" /></Button>
                  </div>
                  <Input type="number" value={it.price} onChange={(e) => setLine(it.key, "price", e.target.value)} className="h-7 w-20 text-right font-mono text-sm" />
                  {hasPerm("discount") && <Input type="number" value={it.discount} onChange={(e) => setLine(it.key, "discount", e.target.value)} placeholder="Disc" className="h-7 w-16 text-right font-mono text-sm" />}
                  <span className="font-mono font-semibold text-sm text-[#1A2A5E] w-20 text-right">{money(it.qty * it.price - (it.discount || 0))}</span>
                </div>
              </div>
            )) : <div className="text-center py-12 text-slate-400 text-sm"><ShoppingCart className="h-10 w-10 mx-auto mb-2 opacity-30" />Cart is empty<br />Tap a favorite or use Add Item</div>}
          </div>
        </ScrollArea>

        <div className="border-t border-slate-100 p-4 space-y-2.5 bg-white rounded-b-xl">
          <div className="flex justify-between text-sm text-slate-600"><span>Subtotal</span><span className="font-mono">{money(totals.subtotal)}</span></div>
          <div className="flex justify-between text-sm text-slate-600"><span>GST</span><span className="font-mono">{money(totals.tax)}</span></div>
          {hasPerm("discount") && <div className="flex items-center justify-between text-sm"><span className="text-slate-600">Bill Discount</span><Input type="number" value={billDiscount} onChange={(e) => update({ billDiscount: e.target.value })} className="h-7 w-24 text-right font-mono" data-testid="bill-discount" /></div>}
          {custom_label && <div className="flex items-center justify-between text-sm"><span className="text-slate-600">{custom_label}</span><Input value={customValue} onChange={(e) => update({ customValue: e.target.value })} className="h-7 w-32 text-right text-xs" data-testid="custom-field" /></div>}
          <div className="flex justify-between items-center pt-1 border-t border-dashed">
            <span className="font-display font-bold text-[#1A2A5E]">TOTAL</span>
            <span className="font-mono font-bold text-2xl text-[#F4571E]" data-testid="grand-total">{money(totals.grand)}</span>
          </div>

          {!quoteMode && (
            <>
              <div className="grid grid-cols-4 gap-1.5 pt-1">
                {["cash", "upi", "card", "credit"].map((m) => (
                  <button key={m} onClick={() => update({ paymentMethod: paymentMethod === m ? "" : m })} data-testid={`pay-${m}`}
                    className={`text-xs font-semibold py-2 rounded-md capitalize transition-colors ${paymentMethod === m ? "bg-[#1A2A5E] text-white" : "bg-slate-100 text-slate-500 hover:bg-slate-200"}`}>{m}</button>
                ))}
              </div>
              {hasPerm("send_bill") && (
                <label className="flex items-center gap-2 text-sm text-slate-600 pt-1 cursor-pointer" data-testid="send-bill-toggle">
                  <input type="checkbox" checked={!!state.sendBill} onChange={(e) => update({ sendBill: e.target.checked })} className="h-4 w-4 accent-[#F4571E]" />
                  Send bill by SMS to customer
                </label>
              )}
              {showPaid && (
                <div className="space-y-1">
                  <div className="flex items-center justify-between text-sm"><span className="text-slate-600">Amount Received</span>
                    <Input type="number" value={paidAmount} onChange={(e) => update({ paidAmount: e.target.value })} className="h-8 w-28 text-right font-mono" data-testid="paid-amount" /></div>
                  <div className="flex justify-between text-sm"><span className="text-slate-600">Balance/Udhaar</span><span className="font-mono text-rose-600">{money(dueAmount)}</span></div>
                </div>
              )}
            </>
          )}

          <Button onClick={checkout} disabled={saving || !cart.length} className="w-full h-12 text-base font-bold bg-[#F4571E] hover:bg-[#D9420D] mt-1" data-testid="checkout-btn">
            {saving ? "Processing..." : quoteMode ? <>Create Quotation</> : <>Complete Sale · {money(totals.grand)}</>}
          </Button>
        </div>
      </Card>

      {/* Add Item search dialog */}
      <Dialog open={addOpen} onOpenChange={setAddOpen}>
        <DialogContent className="bg-white max-w-lg">
          <DialogHeader><DialogTitle className="font-display text-[#1A2A5E]">Add Item</DialogTitle></DialogHeader>
          <Input autoFocus placeholder="Search all products..." value={query} onChange={(e) => setQuery(e.target.value)} data-testid="add-item-search" />
          <div className="max-h-80 overflow-y-auto divide-y">
            {(query ? searchResults : products.slice(0, 30)).map((p) => (
              <button key={p.id} onClick={() => { addToCart(p); toast.success(`${p.name} added`); }} className="w-full flex justify-between items-center py-2.5 hover:bg-slate-50 px-2 text-sm" data-testid={`add-item-${p.id}`}>
                <span>{p.name} <span className="text-slate-400 text-xs">· {p.current_stock} {p.unit}</span></span>
                <span className="font-mono font-semibold text-[#1A2A5E]">{money(p.selling_price)}</span>
              </button>
            ))}
          </div>
          <DialogFooter><Button variant="outline" onClick={() => { setAddOpen(false); setQuery(""); }}>Done</Button></DialogFooter>
        </DialogContent>
      </Dialog>

      <BarcodeScanner open={scanOpen} onClose={() => setScanOpen(false)} onScan={(code) => { setScanOpen(false); addByCode(code); }} />

      <Dialog open={createOpen} onOpenChange={setCreateOpen}>
        <DialogContent className="bg-white max-w-md">
          <DialogHeader><DialogTitle className="font-display text-[#1A2A5E]">Create Item</DialogTitle></DialogHeader>
          <div className="grid grid-cols-[120px_1fr] gap-4">
            <div className="space-y-1">
              <button onClick={() => setNewItem({ ...newItem, is_service: false })} data-testid="create-basic" className={`w-full text-left text-sm px-3 py-2 rounded-lg ${!newItem.is_service ? "bg-[#1A2A5E] text-white" : "bg-slate-100 text-slate-600"}`}>Basic Details</button>
              <button onClick={() => setNewItem({ ...newItem, is_service: true })} data-testid="create-service" className={`w-full text-left text-sm px-3 py-2 rounded-lg ${newItem.is_service ? "bg-[#F4571E] text-white" : "bg-slate-100 text-slate-600"}`}>Service</button>
              <div className="text-[10px] text-slate-400 px-3 pt-1">Advanced: HSN & Tax below. Services are billable but never affect stock.</div>
            </div>
            <div className="space-y-3">
              <div><Label>Item Name *</Label><Input autoFocus value={newItem.name} onChange={(e) => setNewItem({ ...newItem, name: e.target.value })} className="mt-1" data-testid="create-item-name" /></div>
              <div className="grid grid-cols-2 gap-3">
                <div><Label>Price</Label><Input type="number" value={newItem.selling_price} onChange={(e) => setNewItem({ ...newItem, selling_price: e.target.value })} className="mt-1 font-mono" data-testid="create-item-price" /></div>
                <div><Label>Tax %</Label><Input type="number" value={newItem.tax_rate} onChange={(e) => setNewItem({ ...newItem, tax_rate: e.target.value })} className="mt-1 font-mono" /></div>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div><Label>HSN</Label><Input value={newItem.hsn} onChange={(e) => setNewItem({ ...newItem, hsn: e.target.value })} className="mt-1" /></div>
                <div><Label>Unit</Label><Input value={newItem.is_service ? "SVC" : newItem.unit} disabled={newItem.is_service} onChange={(e) => setNewItem({ ...newItem, unit: e.target.value })} className="mt-1" /></div>
              </div>
              {newItem.is_service && <div className="text-xs text-[#F4571E] bg-[#F4571E]/5 rounded px-2 py-1.5">Service item — billable, no inventory/stock tracking.</div>}
            </div>
          </div>
          <DialogFooter><Button variant="outline" onClick={() => setCreateOpen(false)}>Cancel</Button><Button className="bg-[#1A2A5E]" disabled={!newItem.name} onClick={createItem} data-testid="save-create-item">Create & Add to Bill</Button></DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={!!ocrReview} onOpenChange={() => setOcrReview(null)}>
        <DialogContent className="bg-white max-w-2xl max-h-[85vh] overflow-y-auto">
          <DialogHeader><DialogTitle className="text-[#1A2A5E] flex items-center gap-2"><FileText className="h-5 w-5" />Review Imported Bill</DialogTitle></DialogHeader>
          {ocrReview && (
            <div className="space-y-4" data-testid="pos-ocr-review">
              <p className="text-xs text-amber-600 bg-amber-50 rounded-lg px-3 py-2">AI-extracted — verify every value before adding. Nothing is added to the cart until you confirm.</p>
              {ocrReview.preview && <img src={ocrReview.preview} alt="bill" className="max-h-40 rounded border mx-auto" />}
              {ocrReview.vendor_name && <div className="text-sm text-slate-500">Detected vendor: <b>{ocrReview.vendor_name}</b>{ocrReview.invoice_no ? ` · Inv ${ocrReview.invoice_no}` : ""}</div>}
              <div className="space-y-2">
                <div className="grid grid-cols-[1fr_60px_80px_60px_32px] gap-2 text-[10px] text-slate-400 uppercase px-1"><span>Item</span><span className="text-center">Qty</span><span className="text-right">Rate</span><span className="text-center">GST%</span><span /></div>
                {(ocrReview.items || []).map((it, i) => (
                  <div key={i} className="grid grid-cols-[1fr_60px_80px_60px_32px] gap-2 items-center">
                    <Input value={it.name || ""} onChange={(e) => updOcr(i, "name", e.target.value)} className="h-8 text-sm" />
                    <Input type="number" value={it.qty || 0} onChange={(e) => updOcr(i, "qty", e.target.value)} className="h-8 text-center" />
                    <Input type="number" value={it.rate || 0} onChange={(e) => updOcr(i, "rate", e.target.value)} className="h-8 text-right" />
                    <Input type="number" value={it.tax_rate || 0} onChange={(e) => updOcr(i, "tax_rate", e.target.value)} className="h-8 text-center" />
                    <Button size="icon" variant="ghost" className="h-8 w-8" onClick={() => rmOcr(i)}><Trash2 className="h-4 w-4 text-red-500" /></Button>
                  </div>
                ))}
                {!(ocrReview.items || []).length && <p className="text-sm text-slate-400 text-center py-3">No line items detected. Add them manually.</p>}
              </div>
              <div className="flex gap-2 justify-end">
                <Button variant="outline" onClick={() => setOcrReview(null)}>Cancel</Button>
                <Button className="bg-[#1A2A5E]" onClick={applyOcr} data-testid="pos-ocr-add">Add to Cart</Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      <Dialog open={newCustOpen} onOpenChange={setNewCustOpen}>
        <DialogContent className="bg-white">
          <DialogHeader><DialogTitle className="font-display text-[#1A2A5E]">New Customer</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <div><Label>Name *</Label><Input value={newCust.name} onChange={(e) => setNewCust({ ...newCust, name: e.target.value })} className="mt-1" data-testid="new-customer-name" /></div>
            <div className="grid grid-cols-2 gap-3">
              <div><Label>Phone</Label><Input value={newCust.phone} onChange={(e) => setNewCust({ ...newCust, phone: e.target.value })} className="mt-1" /></div>
              <div><Label>Credit Limit</Label><Input type="number" value={newCust.credit_limit} onChange={(e) => setNewCust({ ...newCust, credit_limit: e.target.value })} className="mt-1" /></div>
            </div>
          </div>
          <DialogFooter><Button variant="outline" onClick={() => setNewCustOpen(false)}>Cancel</Button><Button className="bg-[#1A2A5E]" disabled={!newCust.name} onClick={createCustomer} data-testid="save-new-customer">Add</Button></DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={successOpen} onOpenChange={setSuccessOpen}>
        <DialogContent className="bg-white max-w-sm">
          <DialogHeader className="sr-only"><DialogTitle>Complete</DialogTitle></DialogHeader>
          <div className="text-center py-3">
            <div className="h-16 w-16 rounded-full bg-emerald-50 flex items-center justify-center mx-auto mb-3"><CheckCircle2 className="h-9 w-9 text-emerald-500" /></div>
            <h3 className="font-display text-xl font-bold text-[#0F172A]">{lastSale?._quote ? "Quotation Ready!" : "Sale Complete!"}</h3>
            <p className="text-slate-500 text-sm mt-1">{lastSale?._quote ? "QUOTATION" : lastSale?.bill_no}</p>
            <p className="font-mono font-bold text-3xl text-[#F4571E] mt-2">{money(lastSale?.grand_total)}</p>
            <div className="grid grid-cols-3 gap-2 mt-5">
              <Button variant="outline" onClick={() => printThermal(lastSale, biz, bill, printer, lastSale?._quote)} data-testid="print-receipt"><Printer className="h-4 w-4" /></Button>
              <Button variant="outline" onClick={() => printA4(lastSale, biz, bill, lastSale?._quote)} data-testid="print-a4">A4</Button>
              <Button variant="outline" onClick={() => shareWhatsApp(lastSale, biz, "", lastSale?._quote)} data-testid="share-whatsapp"><Share2 className="h-4 w-4" /></Button>
            </div>
            <Button className="w-full mt-2 bg-[#1A2A5E]" onClick={() => { setSuccessOpen(false); setQuoteMode(false); searchRef.current?.focus(); }} data-testid="new-bill-btn">New Bill</Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
